import React from "react";
import { useTheme } from "../contexts/ThemeContext";
import AdminClinicSeeder from "../components/AdminClinicSeeder";

const SystemArchitecturePage = () => {
  const { themeClasses } = useTheme();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div
        className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
      >
        <h1 className={`${themeClasses.text.primary} text-3xl font-bold mb-2`}>
          System Architecture Overview
        </h1>
        <p className={`${themeClasses.text.secondary} text-lg`}>
          Complete Pet AI ID API ecosystem and integration architecture
        </p>
      </div>

      {/* Architecture Diagram */}
      <div
        className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
      >
        <h2 className={`${themeClasses.text.primary} text-2xl font-bold mb-6`}>
          System Architecture Diagram
        </h2>

        <div className="relative w-full h-auto">
          <svg viewBox="0 0 1200 800" className="w-full h-auto">
            {/* Background */}
            <rect
              width="1200"
              height="800"
              fill="currentColor"
              className={themeClasses.text.primary}
              opacity="0.02"
            />

            {/* Client Layer */}
            <g>
              <text
                x="60"
                y="40"
                className={`${themeClasses.text.primary} text-lg font-bold`}
                fill="currentColor"
              >
                Client Applications
              </text>

              {/* iOS App */}
              <rect
                x="50"
                y="60"
                width="120"
                height="80"
                rx="8"
                fill="#007AFF"
                stroke="#0056CC"
                strokeWidth="2"
              />
              <text
                x="110"
                y="85"
                textAnchor="middle"
                fill="white"
                className="text-sm font-semibold"
              >
                iOS App
              </text>
              <text
                x="110"
                y="105"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                Native Swift
              </text>
              <text
                x="110"
                y="120"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                Pet Registration
              </text>

              {/* Web App */}
              <rect
                x="200"
                y="60"
                width="120"
                height="80"
                rx="8"
                fill="#4CAF50"
                stroke="#2E7D32"
                strokeWidth="2"
              />
              <text
                x="260"
                y="85"
                textAnchor="middle"
                fill="white"
                className="text-sm font-semibold"
              >
                Web App
              </text>
              <text
                x="260"
                y="105"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                React/Vite
              </text>
              <text
                x="260"
                y="120"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                Dashboard
              </text>

              {/* White Label */}
              <rect
                x="350"
                y="60"
                width="120"
                height="80"
                rx="8"
                fill="#FF9800"
                stroke="#F57C00"
                strokeWidth="2"
              />
              <text
                x="410"
                y="85"
                textAnchor="middle"
                fill="white"
                className="text-sm font-semibold"
              >
                White Label
              </text>
              <text
                x="410"
                y="105"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                3rd Party
              </text>
              <text
                x="410"
                y="120"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                Integrations
              </text>

              {/* Veterinary Clinics */}
              <rect
                x="500"
                y="60"
                width="120"
                height="80"
                rx="8"
                fill="#9C27B0"
                stroke="#7B1FA2"
                strokeWidth="2"
              />
              <text
                x="560"
                y="85"
                textAnchor="middle"
                fill="white"
                className="text-sm font-semibold"
              >
                Vet Clinics
              </text>
              <text
                x="560"
                y="105"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                Practice
              </text>
              <text
                x="560"
                y="120"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                Management
              </text>
            </g>

            {/* API Gateway Layer */}
            <g>
              <text
                x="60"
                y="220"
                className={`${themeClasses.text.primary} text-lg font-bold`}
                fill="currentColor"
              >
                API Gateway & Authentication
              </text>

              <rect
                x="50"
                y="240"
                width="570"
                height="60"
                rx="8"
                fill="#2196F3"
                stroke="#1976D2"
                strokeWidth="2"
              />
              <text
                x="335"
                y="265"
                textAnchor="middle"
                fill="white"
                className="text-lg font-semibold"
              >
                Firebase API Gateway
              </text>
              <text
                x="335"
                y="285"
                textAnchor="middle"
                fill="white"
                className="text-sm"
              >
                Authentication • Rate Limiting • API Keys • CORS • Request
                Routing
              </text>
            </g>

            {/* Payment Wall */}
            <g>
              <text
                x="680"
                y="220"
                className={`${themeClasses.text.primary} text-lg font-bold`}
                fill="currentColor"
              >
                Payment & Billing
              </text>

              <rect
                x="670"
                y="240"
                width="480"
                height="60"
                rx="8"
                fill="#E91E63"
                stroke="#C2185B"
                strokeWidth="2"
              />
              <text
                x="910"
                y="265"
                textAnchor="middle"
                fill="white"
                className="text-lg font-semibold"
              >
                Payment Wall
              </text>
              <text
                x="910"
                y="285"
                textAnchor="middle"
                fill="white"
                className="text-sm"
              >
                Stripe Integration • Usage Tracking • Subscription Management •
                API Credits
              </text>
            </g>

            {/* Core API Services */}
            <g>
              <text
                x="60"
                y="380"
                className={`${themeClasses.text.primary} text-lg font-bold`}
                fill="currentColor"
              >
                Core API Services (Firebase Functions)
              </text>

              {/* Pet Registration */}
              <rect
                x="50"
                y="400"
                width="140"
                height="100"
                rx="8"
                fill="#4CAF50"
                stroke="#2E7D32"
                strokeWidth="2"
              />
              <text
                x="120"
                y="425"
                textAnchor="middle"
                fill="white"
                className="text-sm font-semibold"
              >
                Pet Registration
              </text>
              <text
                x="120"
                y="445"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • registerPet
              </text>
              <text
                x="120"
                y="460"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • updatePet
              </text>
              <text
                x="120"
                y="475"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • getPetProfile
              </text>
              <text
                x="120"
                y="490"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • QR Generation
              </text>

              {/* Breed Detection */}
              <rect
                x="210"
                y="400"
                width="140"
                height="100"
                rx="8"
                fill="#FF9800"
                stroke="#F57C00"
                strokeWidth="2"
              />
              <text
                x="280"
                y="425"
                textAnchor="middle"
                fill="white"
                className="text-sm font-semibold"
              >
                Breed Detection
              </text>
              <text
                x="280"
                y="445"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • identifyBreed
              </text>
              <text
                x="280"
                y="460"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • Hugging Face AI
              </text>
              <text
                x="280"
                y="475"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • Image Analysis
              </text>
              <text
                x="280"
                y="490"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • Confidence Scoring
              </text>

              {/* Health Records */}
              <rect
                x="370"
                y="400"
                width="140"
                height="100"
                rx="8"
                fill="#9C27B0"
                stroke="#7B1FA2"
                strokeWidth="2"
              />
              <text
                x="440"
                y="425"
                textAnchor="middle"
                fill="white"
                className="text-sm font-semibold"
              >
                Health Records
              </text>
              <text
                x="440"
                y="445"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • extractHealthRecord
              </text>
              <text
                x="440"
                y="460"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • Document AI
              </text>
              <text
                x="440"
                y="475"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • Invoice Processing
              </text>
              <text
                x="440"
                y="490"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • OCR & Analysis
              </text>

              {/* Vaccination */}
              <rect
                x="530"
                y="400"
                width="140"
                height="100"
                rx="8"
                fill="#607D8B"
                stroke="#455A64"
                strokeWidth="2"
              />
              <text
                x="600"
                y="425"
                textAnchor="middle"
                fill="white"
                className="text-sm font-semibold"
              >
                Vaccination
              </text>
              <text
                x="600"
                y="445"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • addVaccination
              </text>
              <text
                x="600"
                y="460"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • getVaccinations
              </text>
              <text
                x="600"
                y="475"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • Passport Gen
              </text>
              <text
                x="600"
                y="490"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • Reminders
              </text>

              {/* User Management */}
              <rect
                x="690"
                y="400"
                width="140"
                height="100"
                rx="8"
                fill="#795548"
                stroke="#5D4037"
                strokeWidth="2"
              />
              <text
                x="760"
                y="425"
                textAnchor="middle"
                fill="white"
                className="text-sm font-semibold"
              >
                User Management
              </text>
              <text
                x="760"
                y="445"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • createUser
              </text>
              <text
                x="760"
                y="460"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • updateProfile
              </text>
              <text
                x="760"
                y="475"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • API Key Mgmt
              </text>
              <text
                x="760"
                y="490"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • Permissions
              </text>

              {/* Analytics */}
              <rect
                x="850"
                y="400"
                width="140"
                height="100"
                rx="8"
                fill="#3F51B5"
                stroke="#303F9F"
                strokeWidth="2"
              />
              <text
                x="920"
                y="425"
                textAnchor="middle"
                fill="white"
                className="text-sm font-semibold"
              >
                Analytics
              </text>
              <text
                x="920"
                y="445"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • Usage Tracking
              </text>
              <text
                x="920"
                y="460"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • Performance
              </text>
              <text
                x="920"
                y="475"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • Error Monitoring
              </text>
              <text
                x="920"
                y="490"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                • Billing Metrics
              </text>
            </g>

            {/* AI Services Layer */}
            <g>
              <text
                x="60"
                y="580"
                className={`${themeClasses.text.primary} text-lg font-bold`}
                fill="currentColor"
              >
                AI & External Services
              </text>

              {/* Google Document AI */}
              <rect
                x="50"
                y="600"
                width="160"
                height="80"
                rx="8"
                fill="#DB4437"
                stroke="#C23321"
                strokeWidth="2"
              />
              <text
                x="130"
                y="625"
                textAnchor="middle"
                fill="white"
                className="text-sm font-semibold"
              >
                Google Document AI
              </text>
              <text
                x="130"
                y="645"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                Invoice Parser
              </text>
              <text
                x="130"
                y="660"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                OCR & Extraction
              </text>

              {/* Hugging Face */}
              <rect
                x="230"
                y="600"
                width="160"
                height="80"
                rx="8"
                fill="#FF6B35"
                stroke="#E55A2B"
                strokeWidth="2"
              />
              <text
                x="310"
                y="625"
                textAnchor="middle"
                fill="white"
                className="text-sm font-semibold"
              >
                Hugging Face AI
              </text>
              <text
                x="310"
                y="645"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                Dog Breed Detection
              </text>
              <text
                x="310"
                y="660"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                Image Classification
              </text>

              {/* Firebase ML */}
              <rect
                x="410"
                y="600"
                width="160"
                height="80"
                rx="8"
                fill="#FFCA28"
                stroke="#FFB300"
                strokeWidth="2"
              />
              <text
                x="490"
                y="625"
                textAnchor="middle"
                fill="white"
                className="text-sm font-semibold"
              >
                Firebase ML
              </text>
              <text
                x="490"
                y="645"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                Custom Models
              </text>
              <text
                x="490"
                y="660"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                AutoML Vision
              </text>

              {/* Cloud Storage */}
              <rect
                x="590"
                y="600"
                width="160"
                height="80"
                rx="8"
                fill="#0F9D58"
                stroke="#0A7B3E"
                strokeWidth="2"
              />
              <text
                x="670"
                y="625"
                textAnchor="middle"
                fill="white"
                className="text-sm font-semibold"
              >
                Cloud Storage
              </text>
              <text
                x="670"
                y="645"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                Image Storage
              </text>
              <text
                x="670"
                y="660"
                textAnchor="middle"
                fill="white"
                className="text-xs"
              >
                Document Archive
              </text>
            </g>

            {/* Database Layer */}
            <g>
              <text
                x="800"
                y="580"
                className={`${themeClasses.text.primary} text-lg font-bold`}
                fill="currentColor"
              >
                Database Layer
              </text>

              <rect
                x="790"
                y="600"
                width="360"
                height="80"
                rx="8"
                fill="#F4B400"
                stroke="#EA9200"
                strokeWidth="2"
              />
              <text
                x="970"
                y="625"
                textAnchor="middle"
                fill="white"
                className="text-lg font-semibold"
              >
                Firebase Firestore
              </text>
              <text
                x="970"
                y="645"
                textAnchor="middle"
                fill="white"
                className="text-sm"
              >
                pets • users • healthRecords • vaccinations • breedInfo •
                apiKeys
              </text>
              <text
                x="970"
                y="665"
                textAnchor="middle"
                fill="white"
                className="text-sm"
              >
                Real-time • Scalable • NoSQL • Security Rules
              </text>
            </g>

            {/* Connection Lines */}
            <g
              stroke="currentColor"
              className={themeClasses.text.secondary}
              strokeWidth="2"
              fill="none"
            >
              {/* Clients to API Gateway */}
              <line x1="110" y1="140" x2="110" y2="240" />
              <line x1="260" y1="140" x2="260" y2="240" />
              <line x1="410" y1="140" x2="410" y2="240" />
              <line x1="560" y1="140" x2="560" y2="240" />

              {/* API Gateway to Payment */}
              <line x1="620" y1="270" x2="670" y2="270" />

              {/* API Gateway to Services */}
              <line x1="335" y1="300" x2="335" y2="400" />
              <line x1="335" y1="350" x2="120" y2="350" />
              <line x1="120" y1="350" x2="120" y2="400" />
              <line x1="335" y1="350" x2="280" y2="350" />
              <line x1="280" y1="350" x2="280" y2="400" />
              <line x1="335" y1="350" x2="440" y2="350" />
              <line x1="440" y1="350" x2="440" y2="400" />
              <line x1="335" y1="350" x2="600" y2="350" />
              <line x1="600" y1="350" x2="600" y2="400" />
              <line x1="335" y1="350" x2="760" y2="350" />
              <line x1="760" y1="350" x2="760" y2="400" />
              <line x1="335" y1="350" x2="920" y2="350" />
              <line x1="920" y1="350" x2="920" y2="400" />

              {/* Services to AI/Storage */}
              <line x1="280" y1="500" x2="310" y2="500" />
              <line x1="310" y1="500" x2="310" y2="600" />
              <line x1="440" y1="500" x2="130" y2="500" />
              <line x1="130" y1="500" x2="130" y2="600" />
              <line x1="600" y1="500" x2="670" y2="500" />
              <line x1="670" y1="500" x2="670" y2="600" />

              {/* Services to Database */}
              <line x1="920" y1="500" x2="970" y2="500" />
              <line x1="970" y1="500" x2="970" y2="600" />
            </g>

            {/* API Endpoints Labels */}
            <g>
              <text
                x="1000"
                y="40"
                className={`${themeClasses.text.primary} text-sm font-semibold`}
                fill="currentColor"
              >
                Key API Endpoints:
              </text>
              <text
                x="1000"
                y="60"
                className={`${themeClasses.text.secondary} text-xs`}
                fill="currentColor"
              >
                POST /registerPet
              </text>
              <text
                x="1000"
                y="75"
                className={`${themeClasses.text.secondary} text-xs`}
                fill="currentColor"
              >
                POST /identifyBreed
              </text>
              <text
                x="1000"
                y="90"
                className={`${themeClasses.text.secondary} text-xs`}
                fill="currentColor"
              >
                POST /extractHealthRecord
              </text>
              <text
                x="1000"
                y="105"
                className={`${themeClasses.text.secondary} text-xs`}
                fill="currentColor"
              >
                POST /addVaccination
              </text>
              <text
                x="1000"
                y="120"
                className={`${themeClasses.text.secondary} text-xs`}
                fill="currentColor"
              >
                GET /getPetProfile/[petId]
              </text>
              <text
                x="1000"
                y="135"
                className={`${themeClasses.text.secondary} text-xs`}
                fill="currentColor"
              >
                GET /getVaccinations/[petId]
              </text>
            </g>
          </svg>
        </div>
      </div>

      {/* Architecture Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Data Flow */}
        <div
          className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
        >
          <h3 className={`${themeClasses.text.primary} text-xl font-bold mb-4`}>
            Data Flow Architecture
          </h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                1
              </div>
              <div>
                <h4 className={`${themeClasses.text.primary} font-semibold`}>
                  Client Request
                </h4>
                <p className={`${themeClasses.text.secondary} text-sm`}>
                  iOS/Web/3rd Party sends API request with authentication
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                2
              </div>
              <div>
                <h4 className={`${themeClasses.text.primary} font-semibold`}>
                  API Gateway
                </h4>
                <p className={`${themeClasses.text.secondary} text-sm`}>
                  Firebase validates auth, checks rate limits, routes request
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                3
              </div>
              <div>
                <h4 className={`${themeClasses.text.primary} font-semibold`}>
                  Payment Check
                </h4>
                <p className={`${themeClasses.text.secondary} text-sm`}>
                  Stripe validates subscription, deducts API credits
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                4
              </div>
              <div>
                <h4 className={`${themeClasses.text.primary} font-semibold`}>
                  Service Processing
                </h4>
                <p className={`${themeClasses.text.secondary} text-sm`}>
                  Firebase Function processes request, calls AI services
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                5
              </div>
              <div>
                <h4 className={`${themeClasses.text.primary} font-semibold`}>
                  Data Storage
                </h4>
                <p className={`${themeClasses.text.secondary} text-sm`}>
                  Results stored in Firestore, files in Cloud Storage
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                6
              </div>
              <div>
                <h4 className={`${themeClasses.text.primary} font-semibold`}>
                  Response
                </h4>
                <p className={`${themeClasses.text.secondary} text-sm`}>
                  Structured JSON response returned to client
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Integration Points */}
        <div
          className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
        >
          <h3 className={`${themeClasses.text.primary} text-xl font-bold mb-4`}>
            Integration Points
          </h3>
          <div className="space-y-4">
            <div>
              <h4 className={`${themeClasses.text.primary} font-semibold mb-2`}>
                Client Applications
              </h4>
              <ul
                className={`${themeClasses.text.secondary} text-sm space-y-1`}
              >
                <li>• iOS App: Native Swift with camera integration</li>
                <li>• Web Dashboard: React/Vite with real-time updates</li>
                <li>• White Label: REST API with custom branding</li>
                <li>• Vet Clinics: Practice management integration</li>
              </ul>
            </div>
            <div>
              <h4 className={`${themeClasses.text.primary} font-semibold mb-2`}>
                Payment Integration
              </h4>
              <ul
                className={`${themeClasses.text.secondary} text-sm space-y-1`}
              >
                <li>• Stripe subscriptions and one-time payments</li>
                <li>• API credit system with usage tracking</li>
                <li>• Automatic billing and invoice generation</li>
                <li>• Usage analytics and cost optimization</li>
              </ul>
            </div>
            <div>
              <h4 className={`${themeClasses.text.primary} font-semibold mb-2`}>
                AI Services
              </h4>
              <ul
                className={`${themeClasses.text.secondary} text-sm space-y-1`}
              >
                <li>• Google Document AI for invoice processing</li>
                <li>• Hugging Face for breed identification</li>
                <li>• Firebase ML for custom model training</li>
                <li>• Cloud Vision for image analysis</li>
              </ul>
            </div>
            <div>
              <h4 className={`${themeClasses.text.primary} font-semibold mb-2`}>
                Security & Scaling
              </h4>
              <ul
                className={`${themeClasses.text.secondary} text-sm space-y-1`}
              >
                <li>• Firebase Authentication with JWT tokens</li>
                <li>• API key management and rate limiting</li>
                <li>• Auto-scaling Firebase Functions</li>
                <li>• CORS and security headers</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* API Pricing Tiers */}
      <div
        className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
      >
        <h3 className={`${themeClasses.text.primary} text-xl font-bold mb-4`}>
          API Pricing Tiers
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-gray-300 rounded-lg p-4">
            <h4
              className={`${themeClasses.text.primary} font-semibold text-lg mb-2`}
            >
              Starter
            </h4>
            <p
              className={`${themeClasses.text.primary} text-2xl font-bold mb-2`}
            >
              $29/month
            </p>
            <ul className={`${themeClasses.text.secondary} text-sm space-y-1`}>
              <li>• 1,000 API calls/month</li>
              <li>• Basic breed detection</li>
              <li>• Standard support</li>
              <li>• 99.5% uptime SLA</li>
            </ul>
          </div>
          <div className="border border-blue-500 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20">
            <h4
              className={`${themeClasses.text.primary} font-semibold text-lg mb-2`}
            >
              Professional
            </h4>
            <p
              className={`${themeClasses.text.primary} text-2xl font-bold mb-2`}
            >
              $99/month
            </p>
            <ul className={`${themeClasses.text.secondary} text-sm space-y-1`}>
              <li>• 10,000 API calls/month</li>
              <li>• Full health record processing</li>
              <li>• Priority support</li>
              <li>• 99.9% uptime SLA</li>
            </ul>
          </div>
          <div className="border border-gray-300 rounded-lg p-4">
            <h4
              className={`${themeClasses.text.primary} font-semibold text-lg mb-2`}
            >
              Enterprise
            </h4>
            <p
              className={`${themeClasses.text.primary} text-2xl font-bold mb-2`}
            >
              Custom
            </p>
            <ul className={`${themeClasses.text.secondary} text-sm space-y-1`}>
              <li>• Unlimited API calls</li>
              <li>• Custom integrations</li>
              <li>• Dedicated support</li>
              <li>• 99.99% uptime SLA</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Norwegian Clinic Seeder - Admin Only */}
      <AdminClinicSeeder />
    </div>
  );
};

export default SystemArchitecturePage;
